/**
 * FreeCoursePage — /free-course?t=<token>
 *
 * Where the day-31 FRWY email lands (worker/src/free-course-notices.ts;
 * thread mct_6a4d0878-eab). The token is the buyer's link; it carries no
 * email. The page lists the programs open today that the buyer does not
 * already have, and "Choose this one" grants it exactly as the order form's
 * "Use my free course" does. Opening the page records nothing: only the
 * button press claims.
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import TakerShell from './components/TakerShell';

const API_BASE = (import.meta as unknown as { env: Record<string, string> }).env?.VITE_API_BASE_URL ?? '';
const ACCENT = '#F05A28';
const MUTED = '#6b7280';

interface Chooser {
  first_name: string | null;
  label: string | null;
  programs: Array<{ slug: string; label: string }>;
  claimed: { program_label: string } | null;
  unlocks_at: string | null;
}

type State =
  | { phase: 'loading' }
  | { phase: 'ready'; data: Chooser }
  | { phase: 'done'; program: string; manual: boolean }
  | { phase: 'error'; message: string };

const card: React.CSSProperties = {
  background: '#fff', border: '1px solid #E2E8F0', borderRadius: 14, padding: '18px 20px', margin: '0 0 12px',
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap',
};
const button: React.CSSProperties = {
  padding: '10px 18px', borderRadius: 8, border: 'none', background: ACCENT, color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer',
};

function day(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { timeZone: 'America/Toronto', weekday: 'long', month: 'long', day: 'numeric' });
}

export default function FreeCoursePage() {
  const [params] = useSearchParams();
  const token = params.get('t') ?? '';
  const [state, setState] = useState<State>({ phase: 'loading' });
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    fetch(`${API_BASE}/api/public/free-course?t=${encodeURIComponent(token)}`)
      .then(async (r) => {
        if (!live) return;
        if (!r.ok) {
          setState({ phase: 'error', message: 'We could not find your free program from this link. Reply to your email and we will sort it out.' });
          return;
        }
        setState({ phase: 'ready', data: (await r.json()) as Chooser });
      })
      .catch(() => live && setState({ phase: 'error', message: 'Something went wrong loading this page. Please try again in a moment.' }));
    return () => { live = false; };
  }, [token]);

  const choose = async (slug: string, label: string) => {
    setBusy(slug);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/public/free-course/claim`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ t: token, program_slug: slug }),
      });
      const data = (await r.json().catch(() => ({}))) as { error?: string; status?: string; program_label?: string };
      if (!r.ok) { setError(data.error ?? 'That did not go through. Please try again.'); return; }
      setState({ phase: 'done', program: data.program_label || label, manual: data.status === 'pending_manual' });
    } catch {
      setError('That did not go through. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <TakerShell>
      <main style={{ maxWidth: 640, margin: '0 auto', padding: '48px 16px 72px' }} data-testid="free-course-page">
        {state.phase === 'loading' && <p style={{ color: MUTED }}>Loading your free program…</p>}

        {state.phase === 'error' && (
          <>
            <h1 style={{ fontSize: 28, margin: '0 0 12px' }}>Your free program</h1>
            <p role="alert">{state.message}</p>
          </>
        )}

        {state.phase === 'done' && (
          <div data-testid="free-course-done">
            <h1 style={{ fontSize: 28, margin: '0 0 12px' }}>You’re in. {state.program} is yours.</h1>
            <p style={{ lineHeight: 1.6 }}>
              {state.manual
                ? 'We’ve used your free program, so there’s nothing to pay. Our team is setting up your access now and will email you within one business day.'
                : 'We’ve used your free program, so there’s nothing to pay. Your welcome email is on its way with everything you need to get started.'}
            </p>
          </div>
        )}

        {state.phase === 'ready' && (() => {
          const d = state.data;
          const verb = d.programs.length === 1 ? 'start' : 'choose';
          const hello = d.first_name ? `${d.first_name}, ${verb} your free program` : `${verb[0].toUpperCase()}${verb.slice(1)} your free program`;
          if (d.claimed) {
            return (
              <div data-testid="free-course-claimed">
                <h1 style={{ fontSize: 28, margin: '0 0 12px' }}>Your free program is already in use</h1>
                <p style={{ lineHeight: 1.6 }}>You chose <strong>{d.claimed.program_label}</strong>. Look for its welcome email, or reply to your email if you can’t find it.</p>
              </div>
            );
          }
          if (d.programs.length === 0) {
            return (
              <div data-testid="free-course-none">
                <h1 style={{ fontSize: 28, margin: '0 0 12px' }}>Your free program</h1>
                <p style={{ lineHeight: 1.6 }}>
                  {d.unlocks_at
                    ? <>Your free program unlocks on <strong>{day(d.unlocks_at)}</strong>, once your 30-day trial is over. Come back then to start it.</>
                    : 'There’s nothing to choose from this link right now. Reply to your email and we’ll help.'}
                </p>
              </div>
            );
          }
          return (
            <>
              <h1 style={{ fontSize: 28, margin: '0 0 8px' }}>{hello}</h1>
              <p style={{ lineHeight: 1.6, margin: '0 0 24px', color: '#334155' }}>
                {d.label ?? 'Your annual BKOS plan includes one free program.'} {d.programs.length === 1 ? 'It’s ready when you are.' : 'Pick the program you want to start.'} There’s nothing to pay.
              </p>
              {d.programs.map((p) => (
                <div key={p.slug} style={card} data-testid={`free-course-option-${p.slug}`}>
                  <strong style={{ fontSize: 17 }}>{p.label}</strong>
                  <button type="button" style={button} disabled={busy !== null} onClick={() => choose(p.slug, p.label)}>
                    {busy === p.slug ? 'Setting it up…' : d.programs.length === 1 ? 'Start it now' : 'Choose this one'}
                  </button>
                </div>
              ))}
              {error && <p role="alert" style={{ color: '#B91C1C', fontSize: 14 }}>{error}</p>}
              <p style={{ color: MUTED, fontSize: 14, marginTop: 20 }}>{d.programs.length === 1 ? 'Reply to your email if you need a hand.' : 'You can choose one program. Once chosen, it can’t be swapped here; reply to your email if you need a hand.'}</p>
            </>
          );
        })()}
      </main>
    </TakerShell>
  );
}
